/**
 * Shop stage move rules (docs/shop/PRD.md §7.2). PURE: no Firestore, no clock. The server gathers
 * the facts and applies the decision in a transaction; the board calls allowedMoves so it only
 * offers moves that will succeed.
 */
import { SHOP_STAGES, TERMINAL_SHOP_STAGES, shopStageOrder } from './stages';
import type { ShopStageKey } from './stages';

export type ShopRole = 'technician' | 'office' | 'manager';
export type ShopMoveActor = ShopRole | 'system';
export type ShopOutcome = 'repaired' | 'returned_unrepaired';

export interface ShopGateFacts {
  hasCustomer: boolean;
  hasUnitIdentity: boolean;
  hasComplaint: boolean;
  approvalRequestsSent: number;
  approvedScopes: number;
  partsOutstanding: number;
  unverifiedRepairedScopes: number;
  outcome: ShopOutcome | null;
  billingPacketReady: boolean;
  billFullyInvoiced: boolean;
}

export const EMPTY_GATE_FACTS: ShopGateFacts = {
  hasCustomer: false, hasUnitIdentity: false, hasComplaint: false,
  approvalRequestsSent: 0, approvedScopes: 0, partsOutstanding: 0, unverifiedRepairedScopes: 0,
  outcome: null, billingPacketReady: false, billFullyInvoiced: false,
};

export interface ShopMoveRequest {
  from: ShopStageKey;
  to: ShopStageKey;
  actor: ShopMoveActor;
  facts: ShopGateFacts;
  reason?: string;
  managerOverride?: boolean;
}

export type ShopMoveDecision =
  | { allowed: true; kind: 'forward' | 'cancel' | 'correction'; override: boolean }
  | { allowed: false; refusal: 'same_stage' | 'terminal' | 'no_rule' | 'role' | 'gate' | 'reason_required'; detail: string };

interface ForwardRule {
  from: readonly ShopStageKey[];
  to: ShopStageKey;
  minRole: ShopMoveActor;
  gate: (facts: ShopGateFacts) => boolean;
  gateText: string;
  requiresReason?: boolean;
  /** A manager may pass a failing gate with a reason. */
  overridable?: boolean;
}

// The two `minRole: 'system'` rules below are unreachable in Phase 0 -- moveStage's ctx.role is
// always technician/office/manager, never 'system', and nothing in Phase 0 calls it as one. They
// are defined now because the shared lifecycle is the one rulebook every phase reads from, and
// Phase 3's approval-request automation is the caller that will fire them (code review, 2026-09-17).
const RULES: readonly ForwardRule[] = [
  { from: ['intake'], to: 'diagnosing', minRole: 'technician', gate: (f) => f.hasCustomer && f.hasUnitIdentity && f.hasComplaint, gateText: 'customer, unit identity and complaint are required' },
  { from: ['diagnosing'], to: 'awaiting_approval', minRole: 'system', gate: (f) => f.approvalRequestsSent > 0, gateText: 'an approval request must be sent' },
  { from: ['diagnosing', 'awaiting_approval'], to: 'approved', minRole: 'system', gate: (f) => f.approvedScopes > 0, gateText: 'a payer scope must be approved' },
  { from: ['approved'], to: 'waiting_on_parts', minRole: 'technician', gate: (f) => f.partsOutstanding > 0, gateText: 'at least one part must be outstanding' },
  { from: ['approved', 'waiting_on_parts'], to: 'ready_for_bench', minRole: 'technician', gate: (f) => f.partsOutstanding === 0, gateText: 'all parts must be received' },
  // overridable: Phase 0 has no approvals feature yet, so approvedScopes is never populated and
  // this gate can never pass on its own -- without an override, NO Phase 0 job could ever reach
  // repairing (code review, 2026-09-17). Phase 3's approvals feature makes the override moot in
  // practice; it stays in the rule table afterward as the same "no valid approval on file" escape
  // hatch every other manager override in this table already is.
  { from: ['diagnosing', 'approved', 'waiting_on_parts', 'ready_for_bench'], to: 'repairing', minRole: 'technician', gate: (f) => f.approvedScopes > 0, gateText: 'at least one payer scope must be approved', overridable: true },
  { from: ['repairing'], to: 'ready_for_pickup', minRole: 'technician', gate: (f) => f.unverifiedRepairedScopes === 0, gateText: 'every repaired scope needs a verification finding', overridable: true },
  { from: ['diagnosing', 'awaiting_approval'], to: 'ready_for_pickup', minRole: 'office', gate: (f) => f.outcome === 'returned_unrepaired', gateText: 'outcome must be returned unrepaired', requiresReason: true },
  { from: ['ready_for_pickup'], to: 'billing_review', minRole: 'office', gate: (f) => f.billingPacketReady, gateText: 'every scope with billable work must have a QuickBooks customer assigned' },
  { from: ['billing_review'], to: 'invoiced', minRole: 'office', gate: (f) => f.billFullyInvoiced, gateText: 'every scope\'s invoice status must read invoiced, mirrored back from Collections' },
];

/**
 * Every forward (from, to) pair RULES actually grants, expanded from each rule's `from` array --
 * this is the mechanical link between RULES and the test suite (code review, 2026-09-17: the two
 * were hand-maintained 1:1 with nothing tying them together, so a future rule could ship with no
 * corresponding test and stay green). lifecycle.test.ts's coverage test asserts every pair here
 * was exercised as a passing forward move somewhere in the suite -- add a rule, the set grows, and
 * an uncovered pair fails the suite instead of shipping silently untested.
 */
export interface ShopForwardPair {
  from: ShopStageKey;
  to: ShopStageKey;
  minRole: ShopMoveActor;
  requiresReason: boolean;
}

export const FORWARD_PAIRS: readonly ShopForwardPair[] = RULES.flatMap((rule) =>
  rule.from.map((from) => ({ from, to: rule.to, minRole: rule.minRole, requiresReason: Boolean(rule.requiresReason) })),
);

const RANK: Readonly<Record<ShopRole, number>> = { technician: 0, office: 1, manager: 2 };

function actorSatisfies(actor: ShopMoveActor, minRole: ShopMoveActor): boolean {
  if (minRole === 'system' || actor === 'system') return actor === minRole;
  return RANK[actor] >= RANK[minRole];
}

const hasReason = (reason?: string): boolean => typeof reason === 'string' && reason.trim().length > 0;

export function decideMove(req: ShopMoveRequest): ShopMoveDecision {
  const { from, to, actor, facts, reason, managerOverride } = req;
  if (from === to) return { allowed: false, refusal: 'same_stage', detail: `already ${from}` };
  if (from === 'cancelled') return { allowed: false, refusal: 'terminal', detail: 'a cancelled job cannot move' };

  // Correction: back to an earlier happy-path stage; the only way out of `invoiced`.
  if (to !== 'cancelled' && shopStageOrder(to) < shopStageOrder(from)) {
    if (actor !== 'manager') return { allowed: false, refusal: 'role', detail: 'only a manager can move a job back' };
    if (!hasReason(reason)) return { allowed: false, refusal: 'reason_required', detail: 'a correction needs a reason' };
    return { allowed: true, kind: 'correction', override: false };
  }

  if (TERMINAL_SHOP_STAGES.has(from)) return { allowed: false, refusal: 'terminal', detail: `${from} only allows manager corrections` };

  if (to === 'cancelled') {
    if (actor !== 'manager') return { allowed: false, refusal: 'role', detail: 'only a manager can cancel a job' };
    if (!hasReason(reason)) return { allowed: false, refusal: 'reason_required', detail: 'cancelling needs a reason' };
    return { allowed: true, kind: 'cancel', override: false };
  }

  const candidates = RULES.filter((r) => r.to === to && r.from.includes(from));
  if (candidates.length === 0) return { allowed: false, refusal: 'no_rule', detail: `no move from ${from} to ${to}` };

  const permitted = candidates.filter((r) => actorSatisfies(actor, r.minRole));
  if (permitted.length === 0) return { allowed: false, refusal: 'role', detail: `${actor} cannot move ${from} to ${to}` };

  let refusal: ShopMoveDecision = { allowed: false, refusal: 'gate', detail: permitted[0].gateText };
  for (const rule of permitted) {
    if (rule.requiresReason && !hasReason(reason)) {
      refusal = { allowed: false, refusal: 'reason_required', detail: 'this move needs a reason' };
      continue;
    }
    if (rule.gate(facts)) return { allowed: true, kind: 'forward', override: false };
    if (rule.overridable && managerOverride) {
      if (actor !== 'manager') return { allowed: false, refusal: 'role', detail: 'only a manager can override this gate' };
      if (!hasReason(reason)) return { allowed: false, refusal: 'reason_required', detail: 'an override needs a reason' };
      return { allowed: true, kind: 'forward', override: true };
    }
    refusal = { allowed: false, refusal: 'gate', detail: rule.gateText };
  }
  return refusal;
}

/**
 * Moves the board may offer. Reason-requiring moves are evaluated with a placeholder reason;
 * the board collects the real reason before calling the server, which re-decides with it.
 */
export function allowedMoves(from: ShopStageKey, actor: ShopMoveActor, facts: ShopGateFacts): ShopStageKey[] {
  return SHOP_STAGES.map((s) => s.key).filter((to) => decideMove({ from, to, actor, facts, reason: 'reason' }).allowed);
}
